import { useCallback, useEffect, useState } from "react";
import { Loader2, Trophy, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stars } from "@/components/Rating";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
const MEDAL = ["bg-amber-400 text-slate-900", "bg-slate-300 text-slate-900", "bg-orange-300 text-slate-900"];

export function Leaderboard() {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/leaderboard", { params: month ? { month } : {} }).then((r) => setD(r.data)), [month]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="leaderboard">
      <div className="p-4 flex flex-wrap items-center gap-3 border-b border-slate-100">
        <Trophy className="h-5 w-5 text-amber-500" />
        <span className="text-xs text-slate-500 flex-1">Score out of 100: rating 50 · speed 30 (faster than 96h) · jobs finished 20</span>
        <Select value={d.month} onValueChange={setMonth}>
          <SelectTrigger className="w-52" data-testid="leaderboard-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{d.months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {d.rows.length === 0 ? <p className="p-5 text-sm text-slate-500" data-testid="leaderboard-empty">No finished jobs in {monthLabel(d.month)}.</p> : (
        <div className="divide-y divide-slate-100">
          {d.rows.map((r, i) => (
            <div key={r.pro_id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`leaderboard-row-${r.pro_id}`}>
              <span className={`h-8 w-8 rounded-full grid place-items-center text-sm font-bold ${MEDAL[i] || "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
              <div className="flex-1 min-w-[160px]"><div className="text-sm font-semibold text-slate-900">{r.pro_name}</div><div className="text-xs text-slate-500">{r.type_label}</div></div>
              <span className="text-xs text-slate-600 w-36 flex items-center gap-1">{r.avg_rating ? <><Stars value={r.avg_rating} size="h-3.5 w-3.5" />{r.avg_rating.toFixed(1)}</> : "No ratings"}</span>
              <span className="text-xs text-slate-600 w-24">{r.avg_hours != null ? `${r.avg_hours}h avg` : "—"}</span>
              <span className="text-xs text-slate-600 w-16">{r.jobs} job{r.jobs === 1 ? "" : "s"}</span>
              <span className="font-display text-2xl font-extrabold text-slate-900 w-14 text-right" data-testid={`leaderboard-score-${r.pro_id}`}>{r.score}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FollowUpDialog({ itemId, onClose, onSent }) {
  const [r, setR] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/ratings/${itemId}`).then(({ data }) => {
      setR(data);
      setMsg(`We're sorry your experience with ${data.pro_name || "your professional"} fell short. We'd love to hear what went wrong and make it right, whether that's a second review at no charge or a quick call with our team.`);
    }).catch((e) => { toast.error(formatErr(e)); onClose(); });
  }, [itemId, onClose]);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/admin/ratings/${itemId}/follow-up`, { message: msg }); toast.success(`Note sent to ${r.user_email}`); onSent?.(); onClose(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="followup-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Contact client</DialogTitle></DialogHeader>
        {!r ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : <>
          <div className="text-sm text-slate-600"><Stars value={r.stars} size="h-4 w-4" /> <b>{r.user_email}</b> · {r.title}{r.comment && <p className="mt-2 italic">"{r.comment}"</p>}</div>
          {r.followed_up_at && <p className="text-xs text-amber-700" data-testid="followup-already-sent">You already sent a note to this client.</p>}
          <Textarea rows={6} maxLength={3000} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="followup-message-input" />
          <p className="text-xs text-slate-500">Sent from GrantVerify. The client's reply comes to your admin inbox.</p>
          <Button disabled={busy || msg.trim().length < 5} onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid="followup-send-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send note</Button>
        </>}
      </DialogContent>
    </Dialog>
  );
}
